// The chat's confirmation cards (thread entries of type 'proposal'): what a
// card is called, and what the assistant is told the coach did with it.
//
// A card holds a change the assistant proposed (server
// mcp/assistantProposals.mjs). Its state: 'pending' (waiting for the coach),
// 'working' (the coach confirmed; the change is being made), then 'done',
// 'cancelled' or 'failed'. A failed card can be confirmed again.

const ROLE_WORDS = { player: 'player', coach: 'coach', staff: 'staff' };

/** One line naming what the card is for (also what the assistant hears). */
export const proposalSummary = (proposal) => {
  switch (proposal?.kind) {
    case 'poll':
      return `poll "${proposal.title}"`;
    case 'roster_add':
      return `add ${proposal.member?.full_name} as a ${ROLE_WORDS[proposal.member?.role] || 'member'}`;
    case 'roster_update':
      return `update ${proposal.before?.full_name}`;
    case 'saved_location':
      return `save "${proposal.addressName}"`;
    default:
      return '';
  }
};

const lower = (value) => String(value ?? '').trim().toLowerCase();

// What a card is about, so a corrected card can stand in for the one it
// corrects: the same poll title, new member's email, member, or place name,
// on the same team.
export const proposalSubjectKey = (proposal) => {
  const team = proposal?.team_id || '';
  switch (proposal?.kind) {
    case 'poll':
      return `${team}:poll:${lower(proposal.title)}`;
    case 'roster_add':
      return `${team}:add:${lower(proposal.member?.email)}`;
    case 'roster_update':
      return `${team}:update:${proposal.member_id || ''}`;
    case 'saved_location':
      return `${team}:place:${lower(proposal.addressName)}`;
    default:
      return null;
  }
};

/**
 * The thread with every card still open for the same thing as `proposal`
 * withdrawn. Told "she's a player, not staff" about a member whose card was
 * still waiting, the assistant put up a corrected card and asked the coach to
 * dismiss the first; both stayed live, and confirming both would have tried
 * to add her twice. A withdrawn card is no outcome to report: the assistant
 * replaced it itself.
 */
export function supersedeOpenProposals(thread, proposal) {
  const subject = proposalSubjectKey(proposal);
  const list = Array.isArray(thread) ? thread : [];
  if (!subject) return list;
  return list.map((entry) =>
    entry?.type === 'proposal' &&
    ['pending', 'failed'].includes(entry.state) &&
    entry.proposal?.id !== proposal.id &&
    proposalSubjectKey(entry.proposal) === subject
      ? { ...entry, state: 'cancelled', superseded: true, reported: true, error: null }
      : entry,
  );
}

export const isSettledProposal = (entry) =>
  entry?.type === 'proposal' && ['done', 'cancelled', 'failed'].includes(entry.state);

const OUTCOME_BY_STATE = { done: 'confirmed', cancelled: 'cancelled', failed: 'failed' };

/**
 * The cards settled since the coach's last message, for the next chat
 * request: [{ entryId, kind, summary, outcome, detail }].
 */
export function unreportedProposalOutcomes(thread) {
  return (Array.isArray(thread) ? thread : [])
    .filter((entry) => isSettledProposal(entry) && entry.reported !== true)
    .map((entry) => ({
      entryId: entry.id,
      kind: entry.proposal?.kind,
      summary: proposalSummary(entry.proposal),
      outcome: OUTCOME_BY_STATE[entry.state],
      detail: entry.outcomeDetail || '',
    }));
}

/** The thread with those cards marked as told to the assistant. */
export function markProposalsReported(thread, entryIds) {
  const ids = new Set(entryIds);
  if (ids.size === 0) return thread;
  return thread.map((entry) => (ids.has(entry.id) ? { ...entry, reported: true } : entry));
}

export const INTERRUPTED_PROPOSAL_MESSAGE =
  'The page reloaded while this was being saved. Check whether it went through before trying again.';

/**
 * A card still 'working' in a thread read back after a reload: the save was
 * under way and its answer is gone. It reads as failed, with a warning to
 * look before confirming again (a second Confirm would make a second poll).
 */
export function settleInterruptedProposals(thread) {
  return (Array.isArray(thread) ? thread : []).map((entry) =>
    entry?.type === 'proposal' && entry.state === 'working'
      ? {
          ...entry,
          state: 'failed',
          reported: false,
          error: INTERRUPTED_PROPOSAL_MESSAGE,
          outcomeDetail: 'The page reloaded while it was being saved, so the app does not know whether it went through. Check with the read tools before saying either way.',
        }
      : entry,
  );
}

// "Fri, Oct 16 at 5:00 PM" for a poll's closing date and time. The date is a
// calendar day: read and printed in UTC so it never shifts for a coach west
// of it.
export const formatProposalDeadline = (closes) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(closes?.date ?? ''));
  const clock = /^(\d{2}):(\d{2})$/.exec(String(closes?.time ?? ''));
  if (!match || !clock) return '';
  const day = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  const dayLabel = day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
  const hour = Number(clock[1]);
  return `${dayLabel} at ${hour % 12 || 12}:${clock[2]} ${hour < 12 ? 'AM' : 'PM'}`;
};
